import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import {
	Background,
	BackgroundVariant,
	Controls,
	type Edge as FlowEdge,
	MarkerType,
	MiniMap,
	type Node as FlowNode,
	ReactFlow,
	ReactFlowProvider,
	type ReactFlowInstance,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { FiDatabase, FiFileText, FiFolder, FiLayers, FiMaximize2, FiMinimize2, FiRefreshCw, FiSearch } from "react-icons/fi";
import { usePageHeader } from "../../hooks/usePageHeader";
import { architectureNodeTypes, type ArchNodeData } from "./ArchitectureNode";
import { ArchitectureNode as ArchData, ArchitectureResponse, getSiteArchitecture } from "./architectureClient";

/**
 * Architecture — a visual node graph of the whole site: a synthetic root branches into the top-level
 * sections, then pages, then dynamic (record-driven) templates, connected by parent→child edges and
 * laid out as a tidy left-to-right tree. Branches collapse/expand so the graph stays readable; dynamic
 * templates carry their record count. Built on React Flow (pan / zoom / minimap), it shows how pages
 * connect — what an editor can't get from the flat tree on the Content page.
 */

const ROOT_ID = "__root__";
const HOME_ID = "__home__";
const COL_GAP = 300; // horizontal distance between tree depths
const ROW_GAP = 70; // vertical distance between sibling leaves

type Tree = ArchData & { _id: string };

const nodeId = (n: ArchData): string => (n.path === "" ? HOME_ID : n.path || n.name);

/** Wrap the API roots under one synthetic root and stamp a stable id on every node. */
const buildTree = (resp: ArchitectureResponse): Tree => {
	const stamp = (n: ArchData): Tree => ({ ...n, _id: nodeId(n), children: n.children.map(stamp) });
	return {
		kind: "root",
		name: "Banque Centrale du Congo",
		path: "",
		children: resp.roots.map(stamp),
		_id: ROOT_ID,
	} as Tree;
};

const labelOf = (n: ArchData): string => {
	if (n.kind === "dynamic" && (n.name === "{path}" || n.name.includes("{"))) {
		return n.contentTypeLabel ? `${n.contentTypeLabel}` : "Pages dynamiques";
	}
	return (n.kind === "page" && n.title) || n.name;
};

const matches = (n: ArchData, q: string): boolean =>
	`${n.name} ${n.title ?? ""} ${n.path} ${n.contentTypeLabel ?? ""}`.toLowerCase().includes(q);

const allBranchIds = (t: Tree, acc: Set<string>): Set<string> => {
	if (t.children.length) {
		acc.add(t._id);
		t.children.forEach((c) => allBranchIds(c as Tree, acc));
	}
	return acc;
};

const Stat: React.FC<{ icon: React.ComponentType<{ className?: string }>; label: string; value: number; accent?: string }> = ({
	icon: Icon,
	label,
	value,
	accent = "text-foreground",
}) => (
	<div className="flex items-center gap-3 rounded-xl border border-border bg-card px-4 py-2.5">
		<div className="flex h-8 w-8 items-center justify-center rounded-lg bg-muted text-muted-foreground">
			<Icon className="h-4 w-4" />
		</div>
		<div className="min-w-0">
			<div className={`text-lg font-bold tabular-nums leading-none ${accent}`}>{value.toLocaleString("fr-FR")}</div>
			<div className="mt-0.5 truncate text-[10px] font-medium uppercase tracking-wide text-muted-foreground">{label}</div>
		</div>
	</div>
);

const Graph: React.FC<{ resp: ArchitectureResponse }> = ({ resp }) => {
	const history = useHistory();
	const tree = useMemo(() => buildTree(resp), [resp]);
	const [query, setQuery] = useState("");
	const rf = useRef<ReactFlowInstance<FlowNode, FlowEdge> | null>(null);

	// Default: only the root is expanded, so the graph opens on the top-level sections (root → sections).
	// Each section is then expanded on demand — keeps the first view readable instead of a giant column.
	const [expanded, setExpanded] = useState<Set<string>>(() => new Set<string>([ROOT_ID]));

	const toggle = useCallback((id: string) => {
		setExpanded((prev) => {
			const next = new Set(prev);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});
	}, []);

	const open = useCallback(
		(n: ArchData) => {
			if (n.kind === "dynamic" && n.contentTypeKey) history.push(`/content/${encodeURIComponent(n.contentTypeKey)}`);
			else if (n.id) history.push(`/page/${encodeURIComponent(n.id)}`);
		},
		[history]
	);

	const q = query.trim().toLowerCase();

	// When searching, keep only branches that contain a match (matches + their ancestors), fully expanded.
	const includeSet = useMemo(() => {
		if (!q) return null;
		const inc = new Set<string>([ROOT_ID]);
		const walk = (n: Tree, ancestors: string[]): boolean => {
			const selfMatch = matches(n, q);
			let childMatch = false;
			for (const c of n.children) childMatch = walk(c as Tree, [...ancestors, n._id]) || childMatch;
			if (selfMatch || childMatch) {
				inc.add(n._id);
				ancestors.forEach((a) => inc.add(a));
				return true;
			}
			return false;
		};
		tree.children.forEach((c) => walk(c as Tree, [ROOT_ID]));
		return inc;
	}, [q, tree]);

	// Lay the visible tree out left-to-right and emit React Flow nodes + edges.
	const { nodes, edges, visibleCount } = useMemo(() => {
		const ns: FlowNode<ArchNodeData>[] = [];
		const es: FlowEdge[] = [];
		let leaf = 0;
		const pos = new Map<string, { x: number; y: number }>();

		const childrenToShow = (n: Tree): Tree[] => {
			if (includeSet) return (n.children as Tree[]).filter((c) => includeSet.has(c._id));
			return expanded.has(n._id) ? (n.children as Tree[]) : [];
		};

		const place = (n: Tree, depth: number): number => {
			const kids = childrenToShow(n);
			let y: number;
			if (kids.length) {
				const ys = kids.map((k) => place(k, depth + 1));
				y = (ys[0] + ys[ys.length - 1]) / 2;
			} else {
				y = leaf * ROW_GAP;
				leaf += 1;
			}
			pos.set(n._id, { x: depth * COL_GAP, y });
			return y;
		};
		place(tree, 0);

		const emit = (n: Tree) => {
			const p = pos.get(n._id)!;
			const kids = childrenToShow(n);
			const isMatch = !!q && matches(n, q);
			ns.push({
				id: n._id,
				type: "arch",
				position: p,
				draggable: false,
				data: {
					label: n._id === ROOT_ID ? n.name : labelOf(n),
					sublabel: n._id === ROOT_ID ? `${resp.totalPages} pages` : n.path ? `/${n.path}` : "/",
					kind: n.kind as ArchNodeData["kind"],
					recordCount: n.recordCount ?? null,
					childCount: n.children.length,
					hasChildren: n.children.length > 0,
					expanded: includeSet ? true : expanded.has(n._id),
					matched: isMatch,
					onToggle: () => toggle(n._id),
					onOpen: () => open(n),
				},
			});
			for (const c of kids) {
				es.push({
					id: `${n._id}->${c._id}`,
					source: n._id,
					target: c._id,
					type: "smoothstep",
					style: { stroke: c.kind === "dynamic" ? "#7dd3fc" : "#cbd5e1", strokeWidth: 1.5 },
					markerEnd: { type: MarkerType.ArrowClosed, width: 12, height: 12, color: c.kind === "dynamic" ? "#38bdf8" : "#94a3b8" },
				});
				emit(c);
			}
		};
		emit(tree);
		return { nodes: ns, edges: es, visibleCount: ns.length };
	}, [tree, expanded, includeSet, q, resp.totalPages, toggle, open]);

	// Refit whenever the set of visible nodes changes (expand/collapse/search).
	useEffect(() => {
		const t = setTimeout(() => rf.current?.fitView({ padding: 0.18, duration: 350 }), 60);
		return () => clearTimeout(t);
	}, [visibleCount]);

	const expandAll = () => setExpanded(allBranchIds(tree, new Set([ROOT_ID])));
	const collapseAll = () => setExpanded(new Set<string>([ROOT_ID]));

	return (
		<div className="space-y-3">
			<div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
				<Stat icon={FiFileText} label="Pages totales" value={resp.totalPages} />
				<Stat icon={FiFolder} label="Pages statiques" value={resp.staticPages} />
				<Stat icon={FiLayers} label="Modèles dynamiques" value={resp.dynamicTemplates} accent="text-sky-600" />
				<Stat icon={FiDatabase} label="Pages générées" value={resp.totalDynamicRecords} accent="text-sky-600" />
			</div>

			<div className="flex flex-wrap items-center gap-2">
				<div className="relative min-w-0 flex-1">
					<FiSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
					<input
						value={query}
						onChange={(e) => setQuery(e.target.value)}
						placeholder="Filtrer le graphe par nom, chemin ou type…"
						className="w-full rounded-lg border border-border bg-card py-2 pl-9 pr-3 text-sm text-foreground outline-none focus:border-primary focus:ring-1 focus:ring-primary"
					/>
				</div>
				<button type="button" onClick={expandAll} className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-card px-3 py-2 text-sm font-medium text-foreground hover:bg-muted">
					<FiMaximize2 className="h-3.5 w-3.5" /> Tout développer
				</button>
				<button type="button" onClick={collapseAll} className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-card px-3 py-2 text-sm font-medium text-foreground hover:bg-muted">
					<FiMinimize2 className="h-3.5 w-3.5" /> Réduire
				</button>
				<button type="button" onClick={() => rf.current?.fitView({ padding: 0.18, duration: 350 })} className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-card px-3 py-2 text-sm font-medium text-foreground hover:bg-muted" title="Recentrer">
					<FiRefreshCw className="h-3.5 w-3.5" />
				</button>
			</div>

			<div className="h-[calc(100vh-260px)] min-h-[520px] overflow-hidden rounded-2xl border border-border bg-[#f8fafc]">
				<ReactFlow
					nodes={nodes}
					edges={edges}
					nodeTypes={architectureNodeTypes}
					onInit={(inst) => {
						rf.current = inst;
						inst.fitView({ padding: 0.18 });
					}}
					fitView
					minZoom={0.1}
					maxZoom={1.5}
					nodesDraggable={false}
					proOptions={{ hideAttribution: true }}
				>
					<Background variant={BackgroundVariant.Dots} gap={22} size={1} color="#e2e8f0" />
					<Controls showInteractive={false} />
					<MiniMap
						pannable
						zoomable
						nodeColor={(n) => {
							const k = (n.data as ArchNodeData)?.kind;
							return k === "dynamic" ? "#38bdf8" : k === "root" ? "#0f172a" : k === "folder" ? "#cbd5e1" : "#94a3b8";
						}}
						className="!bg-white"
					/>
				</ReactFlow>
			</div>
		</div>
	);
};

const Architecture: React.FC = () => {
	usePageHeader({ title: "Architecture" });
	const [data, setData] = useState<ArchitectureResponse | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	const load = async () => {
		setLoading(true);
		setError(null);
		try {
			setData(await getSiteArchitecture("fr"));
		} catch (e: any) {
			setError(e?.response?.status === 401 ? "Non autorisé." : "Impossible de charger l'architecture du site.");
		} finally {
			setLoading(false);
		}
	};
	useEffect(() => {
		load();
	}, []);

	if (loading) {
		return (
			<div className="space-y-3">
				<div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
					{Array.from({ length: 4 }).map((_, i) => (
						<div key={i} className="h-14 animate-pulse rounded-xl border border-border bg-muted/40" />
					))}
				</div>
				<div className="h-[calc(100vh-260px)] min-h-[520px] animate-pulse rounded-2xl border border-border bg-muted/30" />
			</div>
		);
	}
	if (error) return <div className="rounded-xl border border-rose-200 bg-rose-50 p-6 text-sm text-rose-700">{error}</div>;
	if (!data) return null;

	return (
		<ReactFlowProvider>
			<Graph resp={data} />
		</ReactFlowProvider>
	);
};

export default Architecture;
